import BaseService from '../../base/base_service';
import {SYSTEM_EVENTS} from '../../events/events';
import IBlueRibbonService from './blue_ribbon_service_interface';
import {
    BlueRibbonLoginResult,
    LogLevel,
    SDKInterface,
    IBlueRibbonWindowObject
} from './blue_ribbon_api_interfaces';
import BlueRibbonPersonalJackpotEventsHandler from './event_handlers/blue_ribbon_personal_jackpot_events_handler';
import BlueRibbonPartyJackpotEventsHandler from './event_handlers/blue_ribbon_party_jackpot_events_handler';
import {IBlueRibbonEventsHandler} from './event_handlers/blue_ribbon_event_handler_interface';
import {BlueRibbonConnection} from './blue_ribbon_connection';
import {BlueRibbonConnectionProxy} from './blue_ribbon_connection_proxy';
import {
    BlueRibbonExecutionMonitor,
    deleteBlueRibbonLocalStorageKey,
    getBlueRibbonLocalStorageKey,
    setBlueRibbonLocalStorageKey
} from './blue_ribbon_helpers';
import PartyJackpotOpenedGameFlowHandler from './party_jackpot_open_game_flow_handler';
import {
    IBlueRibbonPartyJackpot,
    IBlueRibbonPersonalJackpot,
    PartyJackpotJoinStatus
} from '../../../../common/interfaces/blue-ribbon-party-jackpot.interface';
import {IBlueRibbonConnection} from './blue_ribbon_connection_interface';
import _ from '../../utils/underscore';
import {action, observable} from 'mobx';
import Event from '../../utils/event';
import { FEATURE_NAME_PARTY_JACKPOTS } from '../../../../common/const/ab-testing-names';

const BlueRibbonOperatorIdKey = 'OperatorId';
const PartyJackpotsJoinStatus = 'PartyJackpotsJoinStatus';

export interface IBlueRibbonServiceParams {
    enabled: boolean;
    isAllLiveGamesPromotion: boolean;
    getBlueRibbonTokenUrl: string;
    getBlueRibbonAnonymousTokenUrl: string;
    joinPartyJackpotUrl: string;
    doNotJoinPartyJackpotUrl: string;
    getOperatorIdUrl: string;
    sdkUrl: string;
    personalJackpotPromotionUrl: string;
    partyJackpotPromotionUrl: string;
    apiHost: string;
    logLevel: LogLevel;
}

export default class BlueRibbonService extends BaseService implements IBlueRibbonService {

    static CLASS = "BlueRibbonService";
    private _initialParams: IBlueRibbonServiceParams;

    private _lastIsEnabled: boolean = null;

    private _insufficientFundsSent: boolean = false;

    init(params: IBlueRibbonServiceParams) {
        this._initialParams = params;
        this._subscribe();
        this._setPartyJackpotsJoinStatus(this._getPartyJackpotsJoinStatusFromLocalStorage());
    }

    isEnabled(): boolean {
        if(this._initialParams.enabled) {
            return true;
        }

        const featureData = this.serviceFactory.abTestingService().getAbTestingDataForFeature(FEATURE_NAME_PARTY_JACKPOTS);
        if (featureData) {
            return true;
        } else {
            this.logger.debug(`BlueRibbon feature ${FEATURE_NAME_PARTY_JACKPOTS} is disabled or hasn't been read from token yet`);
            return false;
        }
    }

    private _fireIsEnabledChanged() {
        const isEnabled = this.isEnabled();
        if (this._lastIsEnabled !== isEnabled) {
            this.logger.debug(`BlueRibbon isEnabled changed from ${this._lastIsEnabled} to ${isEnabled}`);
            const isAllLiveGamesPromotion = this._initialParams.isAllLiveGamesPromotion ?? false;
            Event.fire(SYSTEM_EVENTS.BLUE_RIBBON_IS_ENABLED_CHANGED, { isEnabled, isAllLiveGamesPromotion });
            this._lastIsEnabled = isEnabled;
        }
    }

    private _operationExecutionMonitor: BlueRibbonExecutionMonitor;
    private get operationExecutionMonitor() {
        if(!this._operationExecutionMonitor) {
            this._operationExecutionMonitor = new BlueRibbonExecutionMonitor(this.serviceFactory);
        }
        return this._operationExecutionMonitor;
    }

    private _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.ON_ORBIT_INIT_COMPLETE, this._onInitComplete);
    }

    private _onInitComplete() {
        this._fireIsEnabledChanged();

        if (this.isEnabled()) {
            this._connect().then(() => {
                // We start listening for OAUTH_STATE_CHANGED only after we do initial connect
                // This way we avoid duplicate connect when the user does 2 subsequents autologins
                // (click on a game in Sports then closes the Orbit browser and click again on a game in sport)
                // https://dev.azure.com/888holdings/GamingNDL/_workitems/edit/1677717
                this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOAuthStateChange);
            }).catch(err => {
                this._operationExecutionMonitor.error('BlueRibbon connect on init complete failed', err);
                this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOAuthStateChange);
            });
        } else {
            this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOAuthStateChange);
        }
    }

    private _onOAuthStateChange() {
        this._fireIsEnabledChanged();
        
        if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
            this._insufficientFundsSent = false;
        }

        if(this.isEnabled()) {
            this._connect();
        } else {
            this._dispose();
        }
    }

    isConnected(): boolean {
        return this._blueRibbonConnectionProxy?.isConnected || false;
    }

    private _partyJackpotHandler: BlueRibbonPartyJackpotEventsHandler;
    private _personalJackpotHandler: BlueRibbonPersonalJackpotEventsHandler;
    private _blueRibbonConnectionProxy: BlueRibbonConnectionProxy = new BlueRibbonConnectionProxy();

    private _connect(): Promise<void> {

        return this.serviceFactory.oAuth2Service().waitForAutologin().then(() => {
            return this._blueRibbonConnectionProxy.dispose().then(() => {
                this._blueRibbonConnectionProxy = new BlueRibbonConnectionProxy();

                return this._createBlueRibbonSdkCoreManager().then(blueRibbonCoreManager => {
                    const blueRibbonConnection = new BlueRibbonConnection(
                        blueRibbonCoreManager,
                        this.serviceFactory,
                        this._createEventsHandlers(this._blueRibbonConnectionProxy)
                    );

                    this._blueRibbonConnectionProxy.setInnerConnection(blueRibbonConnection);

                }).catch(err => {
                    this.logger.error('Failed to create BlueRibbon.SdkCoreManager instance', err);
                });
            });
        });
    }

    private _dispose(): Promise<void> {
        if (!this._blueRibbonConnectionProxy || !this._blueRibbonConnectionProxy.isConnected) {
            return Promise.resolve();
        }
        
        return this._blueRibbonConnectionProxy.dispose().then(() => {
            // force sending isEnabled changed event after dispose()
            // because some events may be interleaved with reset() call
            this._lastIsEnabled = null;
            this._fireIsEnabledChanged();
        });
    }

    private _createEventsHandlers(blueRibbonConnection: IBlueRibbonConnection): IBlueRibbonEventsHandler[] {
        this._partyJackpotHandler = new BlueRibbonPartyJackpotEventsHandler(this.serviceFactory, blueRibbonConnection, () => this._personalJackpotId);
        let eventsHandlers: IBlueRibbonEventsHandler[] = [this._partyJackpotHandler];

        if(this.serviceFactory.oAuth2Service().isAuthorized()) {
            this._personalJackpotHandler = new BlueRibbonPersonalJackpotEventsHandler(blueRibbonConnection, this.serviceFactory, () => this._personalJackpotId);
            eventsHandlers.push(this._personalJackpotHandler);
        } else {
            this._personalJackpotHandler = null;
        }

        return eventsHandlers;
    }


    get personalJackpotInfo(): IBlueRibbonPersonalJackpot | null {
        return this._personalJackpotHandler?.personalJackPot;
    }


    private _createBlueRibbonSdkCoreManager(): Promise<SDKInterface> {

        const loadSkdPromise = this._loadBlueRibbonSdk();
        const getOperatorIdPromise = this._getOperatorId();

        return Promise.all([getOperatorIdPromise, loadSkdPromise]).then(data => {
            const operatorId: string = data[0];
            const BlueRibbon = this.serviceFactory.windowService().getGlobalProperty('BlueRibbon') as IBlueRibbonWindowObject;
            return new BlueRibbon.SdkCoreManager({
                operatorId: operatorId,
                logLevel: this._initialParams.logLevel,
                baseServiceUrl: this._initialParams.apiHost,
                loginAuthenticatedPlayer: () => this._loginPlayer(this._initialParams.getBlueRibbonTokenUrl, 'loginAuthenticatedPlayer'),
                loginAnonymousPlayer: () => this._loginPlayer(this._initialParams.getBlueRibbonAnonymousTokenUrl, 'loginAnonymousPlayer'),
                withOverlays: false,
                emitWinsInLobby: true
            });
        });
    }

    private _loadBlueRibbonSdkPromise: Promise<void> = null;
    private _loadBlueRibbonSdk(): Promise<void> {
        if (!this._loadBlueRibbonSdkPromise) {
            const executeLoadBlueRibbonSdk = () => this.serviceFactory.scriptLoaderService().loadScript(this._initialParams.sdkUrl, 'blue-ribbon-sdk');
            this._loadBlueRibbonSdkPromise = this.operationExecutionMonitor.execute({
                operationName: 'loadBlueRibbonSdk',
                operationParams:{
                    url: this._initialParams.sdkUrl
                }
            }, executeLoadBlueRibbonSdk);
        }

        return this._loadBlueRibbonSdkPromise;
    }

    private _getOperatorId(): Promise<string> {
        const operatorId = this._getOperatorIdFromLocalStorage();
        if(operatorId) {
            return Promise.resolve(operatorId);
        }
        const subBrandId = this.serviceFactory.skinService().getSubBrandID();
        const getOperatorIdParams = {
            url: this._initialParams.getOperatorIdUrl,
            request: {subBrandId: subBrandId}
        }
        const executeGetOperatorId = () => {
            return this.serviceFactory.http2Service().post<string>(getOperatorIdParams.url, getOperatorIdParams.request).then(operatorId => {
                this._setOperatorIdInLocalStorage(operatorId);
                return operatorId;
            });
        };
        return this.operationExecutionMonitor.execute({
                operationName: 'getOperatorId',
                operationParams: getOperatorIdParams
            },
            executeGetOperatorId);
    }

    private _personalJackpotId: string = null;
    @observable.ref private _partyJackpotsJoinStatus: Record<string, PartyJackpotJoinStatus> = {};

    @action.bound
    private _setPartyJackpotsJoinStatus(newStatus: Record<string, PartyJackpotJoinStatus>) {
        this._setPartyJackpotsJoinStatusInLocalStorage(newStatus);
        this._partyJackpotsJoinStatus = newStatus || {};
    }

    private _loginPlayer(url: string, operationName: string): Promise<any> {

        const executeLogin = () => this.serviceFactory.http2Service().post<BlueRibbonLoginResult>(url);

        return this.operationExecutionMonitor.execute({
                operationName: operationName,
                logExecutionTimeToElastic: true
            }, executeLogin)
            .then((loginData: BlueRibbonLoginResult) => {
                if (!loginData) {
                    throw new Error(`Adapter login player response is empty for url ${url}`);
                }
                this._personalJackpotId = loginData.GameCode;
                if(this.serviceFactory.oAuth2Service().isAuthorized()) {
                    this._setPartyJackpotsJoinStatus(loginData.JackpotsJoinStatus);
                }

                const info = JSON.parse(loginData.LoginResponse);
                return info;
            }
        );
    }

    getPartyJackpotJoinStatus(jackpotId: string): PartyJackpotJoinStatus {
        if(_.isNullOrUndefined(this._partyJackpotsJoinStatus[jackpotId])) {
            return PartyJackpotJoinStatus.Neutral;
        }
        return this._partyJackpotsJoinStatus[jackpotId];
    }

    getPartyJackpotByGameType(gameType: number): Promise<IBlueRibbonPartyJackpot> {
        return this._blueRibbonConnectionProxy.waitForConnect().then(() => {
            const allJPs = this._partyJackpotHandler.getAllPartyJackpots();
            for (let i = 0; i < allJPs.length; i++) {
                const jackpot = allJPs[i];
                if(jackpot.hasGame(gameType)) {
                    return jackpot;
                }
            }
            return null;
        });
    }

    showPersonalJackpotPromotionPage() {
        if (this._initialParams.personalJackpotPromotionUrl) {
            this.serviceFactory.windowService().redirect(this._initialParams.personalJackpotPromotionUrl);
        }
    }

    showPartyJackpotPromotionPage() {
        if (this._initialParams.partyJackpotPromotionUrl) {
            this.serviceFactory.windowService().redirect(this._initialParams.partyJackpotPromotionUrl);
        }
    }

    createPartyJackpotOpenedGameHandler(gameType: number): PartyJackpotOpenedGameFlowHandler {
        return PartyJackpotOpenedGameFlowHandler.create({
            serviceFactory: this.serviceFactory,
            blueRibbonConnection: this._blueRibbonConnectionProxy,
            gameType: gameType
        })
    }

    private _getOperatorIdLocalStorageKey() {
        // this is necessary because in mirage under the same domain we can load multiple sub brands using the launcher
        return BlueRibbonOperatorIdKey + '_' + this.serviceFactory.skinService().getSubBrandID();
    }
    private _setOperatorIdInLocalStorage(operatorId: string): void {
        if(operatorId) {
            setBlueRibbonLocalStorageKey(this._getOperatorIdLocalStorageKey(), operatorId);
        } else {
            deleteBlueRibbonLocalStorageKey(this._getOperatorIdLocalStorageKey());
        }
    }

    private _getOperatorIdFromLocalStorage(): string {
        return getBlueRibbonLocalStorageKey(this._getOperatorIdLocalStorageKey());
    }

    private _setPartyJackpotsJoinStatusInLocalStorage(jackpotsStatuses: Record<string, PartyJackpotJoinStatus>) {
        if(_.isEmpty(jackpotsStatuses)) {
            deleteBlueRibbonLocalStorageKey(PartyJackpotsJoinStatus);
        } else {
            setBlueRibbonLocalStorageKey(PartyJackpotsJoinStatus, JSON.stringify(jackpotsStatuses));
        }
    }

    private _getPartyJackpotsJoinStatusFromLocalStorage(): Record<string, PartyJackpotJoinStatus> {
        const statuses = getBlueRibbonLocalStorageKey(PartyJackpotsJoinStatus);
        if(statuses) {
            try {
                return JSON.parse(statuses);
            } catch (err) {
                this.logger.error('Failed to parse party jackpot join status from local storage', err);
                return {};
            }
        } else {
            return {};
        }

    }

    private _isPartyJackpotJoinStatusNeutral(partyJackpotId: string): boolean {
        return this.getPartyJackpotJoinStatus(partyJackpotId) === PartyJackpotJoinStatus.Neutral;
    }

    makePartyJackpotStatusNeutral(jackpotId: string): void {
        let clonedJackpotStatuses = {...this._partyJackpotsJoinStatus};
        delete clonedJackpotStatuses[jackpotId];
        this._setPartyJackpotsJoinStatus(clonedJackpotStatuses);
    }

    showPartyJackpotWelcomePopup(partyJackpot: IBlueRibbonPartyJackpot): Promise<PartyJackpotJoinStatus> {
        if(!partyJackpot.isActive) {
            return Promise.resolve(PartyJackpotJoinStatus.NotJoined);
        }

        const currentStatus = this.getPartyJackpotJoinStatus(partyJackpot.jackpotId);

        if(this.serviceFactory.walletService().getCashBalance() === 0) {
            if (currentStatus !== PartyJackpotJoinStatus.NotJoined) {
                this._showInsufficientRealMoneyToaster(partyJackpot);
                return Promise.resolve(PartyJackpotJoinStatus.Neutral);
            }
        }

        if (!this._isPartyJackpotJoinStatusNeutral(partyJackpot.jackpotId)) {
            return Promise.resolve(currentStatus);
        }

        return new Promise<PartyJackpotJoinStatus>((resolve, reject) => {
            this.serviceFactory.orbitComponentRenderService().showPartyJackpotWelcomePopup({
                partyJackpot: partyJackpot,
                onAcceptButtonClicked: () => {
                    this.serviceFactory.http2Service().post(this._initialParams.joinPartyJackpotUrl, {
                        jackpotId: partyJackpot.jackpotId
                    }).then(() => {
                        this._setPartyJackpotsJoinStatus({...this._partyJackpotsJoinStatus, [partyJackpot.jackpotId]: PartyJackpotJoinStatus.Joined})
                        resolve(PartyJackpotJoinStatus.Joined);
                    }).catch(err => {
                        this.logger.error(`Failed to join player to party jackpot ${partyJackpot.jackpotId}`, err);
                        reject(err);
                    })
                },
                onDeclineButtonClicked: () => {
                    if (!partyJackpot.hasRealMoneyWinPrize) {
                        this.serviceFactory.http2Service().post(this._initialParams.doNotJoinPartyJackpotUrl, {
                            jackpotId: partyJackpot.jackpotId
                        }).then(() => {
                            this._setPartyJackpotsJoinStatus({...this._partyJackpotsJoinStatus, [partyJackpot.jackpotId]: PartyJackpotJoinStatus.NotJoined})
                            resolve(PartyJackpotJoinStatus.NotJoined);
                        }).catch(err => {
                            reject(err);
                        });
                    } else {
                        resolve(PartyJackpotJoinStatus.Neutral);
                    }
                }
            });
        });
    }

    private _showInsufficientRealMoneyToaster(partyJackpot: IBlueRibbonPartyJackpot) {
        if (!this._insufficientFundsSent) {
            Event.fire(SYSTEM_EVENTS.BLUE_RIBBON_PARTY_JACKPOT_INSUFFICIENT_MONEY, partyJackpot);
            this._insufficientFundsSent = true;
        } else {
            this.logger.debug("BlueRibbon 0 ballance detected, insufficient toaster already sent once in this session");
        }
    }
}
